import React from 'react';
import classNames from 'classnames';
import { useIsLoggedOut } from '@tokenized/sdk-react-private';
import { FormattedMessage, useIntl } from 'react-intl';
import { getThemeVariableValue, getAppName } from '#app/containers/ThemeLoader';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { DialogBody } from '#components/Elements/Dialog/Dialog';
import Spinner from '#components/Elements/Spinner/Spinner';
import DefaultLayout from '#components/Layouts/DefaultLayout/DefaultLayout';
import { Field as FormField, Form } from 'react-final-form';
import { errorResponseNotify } from '#modules/utils/errors';
import { EntityContext } from '#modules/context/EntityContext';
import {
  PasswordHints,
  passwordsMatch,
  passwordRequirementsMet,
} from '#components/Elements/PasswordHints/PasswordHints';
import {
  useRedirect,
  usePasswordResetRedirect,
} from '#modules/hooks/navigation';
import Label from '#components/Elements/Form/Field/Label';
import Input from '#components/Elements/Form/Controls/Input';
import Button from '#components/Elements/Button/Button';
import EyeIcon from '#components/Elements/SVGIcon/EyeIcon';
import EyeOffIcon from '#components/Elements/SVGIcon/EyeOffIcon';
import PasskeyOfferModal from '#components/Elements/PasskeyOfferModal/PasskeyOfferModal';
import DeviceFingerprintService from '#modules/utils/deviceFingerprint';
import useLiquidNobleApi from '#modules/utils/useLiquidNobleApi';
import { MFA_TYPE_EMAIL } from '#modules/api/User';
import {
  saveAuthToken,
  isLiquidNobleLoggedIn,
  saveEntityId,
  saveProfileId,
  saveUnlockingKey,
  getSessionUsername,
  saveSessionUsername,
} from '#modules/utils/jwt';
import ChevronLeftIcon from '#components/Elements/SVGIcon/ChevronLeftIcon';
import {
  useValidators,
  fieldIsRequired,
  QUIET_VALIDATION_FAILURE,
} from '#modules/utils/validatorsLocalized';
import {
  trackEvent,
  trackLeadGeneration,
  getClientMetadata,
} from '#modules/utils/analytics';
import { usePersistentGlobalState } from '#modules/utils/usePersistentState';
import usePasskey from '#modules/hooks/usePasskey';

import styles from './LogIn.scss';

const rememberReferralCode = (referralCode) => {
  if (!referralCode || referralCode.length > 64) {
    return;
  }
  window.localStorage.setItem('__REFERRAL__', JSON.stringify(referralCode));
};

export default function LogInPage() {
  const termsUrl = getThemeVariableValue('terms-url');
  const privacyUrl = getThemeVariableValue('privacy-url');
  const amlUrl = getThemeVariableValue('aml-ctf-url');
  const cookieUrl = getThemeVariableValue('cookies-url');

  const { executeRecaptcha } = useGoogleReCaptcha();
  const isLoggedOut = useIsLoggedOut();
  const { registerPasskey, getPasskeyAssertion, isPasskeyAvailable } =
    usePasskey();
  const intl = useIntl();
  const liquidNobleApi = useLiquidNobleApi();
  const redirect = useRedirect();
  const passwordRedirect = usePasswordResetRedirect();
  const { setActiveLockboxId } = React.useContext(EntityContext);
  const searchParams = new URLSearchParams(window?.location?.search);
  const [loading, setLoading] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);
  const [registering, setRegistering] = React.useState(false);
  const [mfaRequested, setMfaRequested] = React.useState(false);
  const [usernameChecked, setUsernameChecked] = React.useState(false);
  const [fieldVisibility, setFieldVisibility] = React.useState({});
  const [passkeyOffered, setPasskeyOffered] = usePersistentGlobalState(
    'passkeyOffered',
    false,
  );
  const [showPasskeyModal, setShowPasskeyModal] = React.useState(false);
  const [onModalResult, setOnModalResult] = React.useState(null);
  const [registeringPasskey, setRegisteringPasskey] = React.useState(false);
  const [fingerprint, setFingerprint] = React.useState(null);

  // Bumped on every login attempt and reset. A passkey assertion that
  // resolves late (some mobile devices hang on the WebAuthn prompt) is
  // discarded if a newer attempt or reset has happened in the meantime.
  const authAttemptRef = React.useRef(0);

  React.useEffect(() => {
    const collectFingerprint = async () => {
      if (fingerprint !== null) {
        return; // Fingerprint already collected
      }
      const service = new DeviceFingerprintService();
      const result = await service.getFingerprint();
      setFingerprint(result);
    };

    collectFingerprint();
  }, [fingerprint, setFingerprint]);

  const [queryHandle, setQueryHandle] = React.useState(
    searchParams.get('handle') ?? '',
  );
  const sessionUsername = getSessionUsername();
  const initialValues = React.useMemo(
    () => ({
      username: sessionUsername || queryHandle || '',
      password: '',
      mfaCode: '',
    }),
    [queryHandle, sessionUsername],
  );

  const promptPasskeyOffer = React.useCallback(
    (force = false) => {
      return new Promise((resolve) => {
        if (!force && passkeyOffered) {
          return resolve(false);
        }

        setShowPasskeyModal(true);
        setOnModalResult(() => (choice) => {
          setShowPasskeyModal(false);
          if (!choice) {
            setPasskeyOffered(true);
          }
          resolve(choice);
        });
      });
    },
    [passkeyOffered, setPasskeyOffered],
  );

  const onSignUp = React.useCallback((formProps) => {
    formProps?.form?.reset();
    setRegistering(true);
    setUsernameChecked(false);
  }, []);

  const toggleFieldVisibility = (field) => {
    setFieldVisibility({
      ...fieldVisibility,
      [field]: !fieldVisibility[field],
    });
  };

  const instantSignUp = searchParams.get('signup') === 'true';
  const referralCode = searchParams.get('referral');
  const isLoggedIn = isLiquidNobleLoggedIn();
  const appName = getAppName();

  const validateRequired = useValidators(fieldIsRequired);

  const onRegisterAccount = React.useCallback(
    async (username, password, form) => {
      const reRaptchaToken = await executeRecaptcha?.('auth');

      // Capture client metadata for Meta Conversions API
      const clientMetadata = getClientMetadata();
      console.log('Registering with client_metadata:', clientMetadata);

      const postData = {
        email: username,
        password,
        recaptcha_token: reRaptchaToken,
        fp: fingerprint,
        client_metadata: clientMetadata,
      };
      try {
        const response = await liquidNobleApi.post('/user/register/', postData);
        const { data } = response;
        if (data?.token) {
          saveAuthToken(data.token);
          saveEntityId(data?.entity_id);
          saveProfileId(data?.profile_id);
          saveUnlockingKey(data?.unlocking_key);
          setActiveLockboxId(data?.lockbox_id);
        }
        if (data?.created) {
          trackEvent('Lead', {
            status: true,
            content_name: 'unverified_email_registered',
          });
          trackLeadGeneration();
          form.reset();
        }
        if (data?.code_sent) {
          redirect('/verify-email');
        } else {
          redirect('/dashboard');
        }
      } catch (error) {
        errorResponseNotify(error);
        console.log("'/user/register/' error: ", error.response);
      }
    },
    [
      liquidNobleApi,
      redirect,
      setActiveLockboxId,
      executeRecaptcha,
      fingerprint,
    ],
  );

  const onAuthenticateCredentials = React.useCallback(
    async (username, password, mfaCode, assertionResponse) => {
      authAttemptRef.current += 1;
      const postData = {
        username,
        password,
        mfa_code: mfaCode.replace(/\s/g, ''),
        passkey_assertion: assertionResponse,
        fp: fingerprint,
      };
      try {
        const response = await liquidNobleApi.post(
          '/user/authenticate/',
          postData,
        );
        const { data } = response;
        const userId = data?.user_id;
        let hasPasskey = !!assertionResponse;
        if (!hasPasskey) {
          hasPasskey = await isPasskeyAvailable({
            userId,
          });
        }

        if (data?.token) {
          let wantsPasskey = false;
          if (!hasPasskey && data.mfa_type_selected) {
            wantsPasskey = await promptPasskeyOffer();

            if (wantsPasskey) {
              setRegisteringPasskey(true);
              const success = await registerPasskey({
                userId: data.user_id,
                email: username,
                displayName: username,
              });

              if (success) {
                console.log('✅ Passkey registered successfully');
              } else {
                console.warn('⚠️ Passkey registration failed or was skipped');
              }
              setRegisteringPasskey(false);
            }
          }

          saveSessionUsername(username);
          saveAuthToken(data.token);
          saveEntityId(data?.entity_id);
          saveProfileId(data?.profile_id);
          saveUnlockingKey(data?.unlocking_key);
          setActiveLockboxId(data?.lockbox_id);
          if (!hasPasskey || !wantsPasskey) {
            redirect('/dashboard');
          }
        } else if (data?.status === 'mfa_required') {
          setMfaRequested(data.mfa_type);
          // Unblock the form immediately. The passkey attempt below runs in
          // the background and may never settle on some mobile devices, so
          // it must not hold the submit state open.
          setSubmitting(false);
          if (hasPasskey && !assertionResponse) {
            const currentAttempt = authAttemptRef.current;
            getPasskeyAssertion({ userId: data.user_id })
              .then((assertion) => {
                // Only auto-login with the passkey if the user has not
                // already submitted the MFA code or gone back in the meantime.
                if (assertion && authAttemptRef.current === currentAttempt) {
                  onAuthenticateCredentials(
                    username,
                    password,
                    mfaCode,
                    assertion,
                  );
                }
              })
              .catch(() => {
                // Passkey retrieval failed or was cancelled. The user can
                // fall back to entering the MFA code manually.
              });
          }
        }
      } catch (error) {
        errorResponseNotify(error);
      }
    },
    [
      liquidNobleApi,
      redirect,
      setActiveLockboxId,
      isPasskeyAvailable,
      registerPasskey,
      getPasskeyAssertion,
      promptPasskeyOffer,
      fingerprint,
      setSubmitting,
    ],
  );

  const onCheckUsername = React.useCallback(
    async (username) => {
      const postData = {
        username,
        registration: false,
      };
      try {
        const response = await liquidNobleApi.post(
          '/user/username/check/',
          postData,
        );
        const { data } = response;
        if (data?.status === 'success') {
          // console.log('Handle checked successfully. data: ', data);
          setUsernameChecked(true);
          setSubmitting(false);
        }
      } catch (error) {
        errorResponseNotify(error);
        console.log("'/user/username/check/' error: ", error.response);
        setUsernameChecked(false);
        setSubmitting(false);
      }
    },
    [liquidNobleApi],
  );

  const onSubmit = React.useCallback(
    async (values, form) => {
      const { username, password, mfaCode } = values;

      if (submitting) {
        return;
      }
      setSubmitting(true);
      if (registering) {
        if (username && password && passwordRequirementsMet(password)) {
          await onRegisterAccount(username, password, form);
        }
      } else {
        if (username && !password) {
          await onCheckUsername(username);
        } else if (username && password) {
          await onAuthenticateCredentials(username, password, mfaCode);
        }
      }
      setTimeout(() => {
        setSubmitting(false);
      }, 2000);
    },
    [
      registering,
      onCheckUsername,
      onRegisterAccount,
      submitting,
      setSubmitting,
      onAuthenticateCredentials,
    ],
  );

  React.useEffect(() => {
    const prefilledHandle = sessionUsername || queryHandle;
    if (prefilledHandle) {
      setSubmitting(true);
      onCheckUsername(prefilledHandle);
    }
  }, [queryHandle, sessionUsername, onCheckUsername]);

  const passwordResetRedirect = React.useCallback(
    (username) => {
      passwordRedirect(username);
    },
    [passwordRedirect],
  );

  const onResetLoginState = React.useCallback(() => {
    // Invalidate any in-flight passkey assertion so a late response cannot
    // trigger a login after the user has gone back.
    authAttemptRef.current += 1;
    setRegistering(false);
    setUsernameChecked(false);
    setMfaRequested(false);
    setQueryHandle('');
  }, []);

  React.useEffect(() => {
    rememberReferralCode(referralCode);

    if (isLoggedIn) {
      redirect('/dashboard');
      return;
    }

    if (!instantSignUp) {
      return;
    }
    onSignUp();
  }, [instantSignUp, referralCode, redirect, isLoggedIn, onSignUp]);

  React.useEffect(() => {
    if (!isLoggedOut || !loading) {
      return;
    }

    setTimeout(() => {
      setLoading(false);
    }, 1000);
  }, [loading, setLoading, isLoggedOut]);

  if (loading || !executeRecaptcha) {
    return (
      <div className={styles.loginSpinner}>
        <Spinner animation="pulse" />
      </div>
    );
  }

  return (
    <DefaultLayout
      showNavigation={false}
      enableServiceBanner
      allowModalBackgroundInteraction
      showAppVersionOverForcedModal={false}
      modalTest="login"
      forceModalContent={
        <>
          <PasskeyOfferModal
            visible={showPasskeyModal}
            onConfirm={() => onModalResult?.(true)}
            onCancel={() => onModalResult?.(false)}
          />
          {registeringPasskey && (
            <div className={styles.loginSpinner}>
              <Spinner />
            </div>
          )}
          {!showPasskeyModal && !registeringPasskey && (
            <DialogBody className={styles.loginBody}>
              <Form
                onSubmit={onSubmit}
                initialValues={initialValues}
                render={({ handleSubmit, ...formProps }) => (
                  <>
                    <div className={styles.loginHeader}>
                      {!registering ? (
                        <>
                          <div className={styles.centeredButton}>
                            {usernameChecked && !mfaRequested && (
                              <>
                                <button
                                  className={classNames(
                                    styles.transparentButton,
                                    styles.centeredButton,
                                  )}
                                  onClick={() => {
                                    formProps.form.reset();
                                    onResetLoginState();
                                  }}
                                >
                                  <ChevronLeftIcon
                                    className={styles.backChevron}
                                  />
                                </button>
                                <FormattedMessage defaultMessage="Enter your password" />{' '}
                              </>
                            )}
                            {usernameChecked && !!mfaRequested && (
                              <>
                                <button
                                  className={classNames(
                                    styles.transparentButton,
                                    styles.centeredButton,
                                  )}
                                  onClick={() => {
                                    formProps.form.reset();
                                    onResetLoginState();
                                  }}
                                >
                                  <ChevronLeftIcon
                                    className={styles.backChevron}
                                  />
                                </button>
                                <FormattedMessage defaultMessage="Enter verification code" />{' '}
                              </>
                            )}
                            {!usernameChecked && (
                              <>
                                <FormattedMessage defaultMessage="Welcome" />{' '}
                              </>
                            )}
                          </div>
                          {!!mfaRequested && (
                            <div className={styles.headerSubtitle}>
                              {mfaRequested === MFA_TYPE_EMAIL ? (
                                <FormattedMessage defaultMessage="Enter the verification code sent to your email address:" />
                              ) : (
                                <FormattedMessage defaultMessage="Enter the MFA verification code from your mobile authenticator app:" />
                              )}
                            </div>
                          )}
                          {!usernameChecked && (
                            <div className={styles.headerSubtitle}>
                              <FormattedMessage
                                defaultMessage="Sign in using your {app} account:"
                                values={{
                                  app: appName,
                                }}
                              />
                            </div>
                          )}
                        </>
                      ) : (
                        <div className={styles.createAccountRow}>
                          <button
                            className={classNames(
                              styles.transparentButton,
                              styles.centeredButton,
                            )}
                            onClick={() => {
                              formProps.form.reset();
                              onResetLoginState();
                            }}
                          >
                            <ChevronLeftIcon className={styles.backChevron} />
                          </button>
                          <FormattedMessage defaultMessage="Create new account" />
                        </div>
                      )}
                    </div>
                    <div>
                      <form
                        onSubmit={handleSubmit}
                        className={styles.loginForm}
                      >
                        <FormField name="username" validate={validateRequired}>
                          {({
                            input,
                            meta: {
                              touched,
                              error,
                              submitError,
                              dirtySinceLastSubmit,
                            },
                          }) => (
                            <Label>
                              {registering ? (
                                <FormattedMessage defaultMessage="Email address" />
                              ) : (
                                <FormattedMessage defaultMessage="Email address or handle" />
                              )}
                              <Input
                                disableOnFocus
                                type="email"
                                readOnly={!registering && usernameChecked}
                                placeholder={
                                  registering
                                    ? intl.formatMessage({
                                        defaultMessage: 'Email address',
                                      })
                                    : intl.formatMessage({
                                        defaultMessage:
                                          'Email address or handle',
                                      })
                                }
                                size="medium"
                                maxLength={128}
                                color={
                                  (touched &&
                                    error &&
                                    error !== QUIET_VALIDATION_FAILURE) ||
                                  (submitError && !dirtySinceLastSubmit)
                                    ? 'danger'
                                    : null
                                }
                                {...input}
                              />
                            </Label>
                          )}
                        </FormField>
                        {!registering && usernameChecked && (
                          <FormField
                            name="password"
                            validate={validateRequired}
                          >
                            {({
                              input,
                              meta: {
                                touched,
                                error,
                                submitError,
                                dirtySinceLastSubmit,
                              },
                            }) => (
                              <Label>
                                <FormattedMessage defaultMessage="Password" />
                                <Input
                                  disableOnFocus
                                  readOnly={
                                    !registering &&
                                    usernameChecked &&
                                    mfaRequested
                                  }
                                  type="password"
                                  placeholder={intl.formatMessage({
                                    defaultMessage: 'Password',
                                  })}
                                  size="medium"
                                  maxLength={128}
                                  color={
                                    (touched &&
                                      error &&
                                      error !== QUIET_VALIDATION_FAILURE) ||
                                    (submitError && !dirtySinceLastSubmit)
                                      ? 'danger'
                                      : null
                                  }
                                  {...input}
                                />
                              </Label>
                            )}
                          </FormField>
                        )}
                        {!registering && usernameChecked && mfaRequested && (
                          <FormField name="mfaCode" validate={validateRequired}>
                            {({
                              input,
                              meta: {
                                touched,
                                error,
                                submitError,
                                dirtySinceLastSubmit,
                              },
                            }) => (
                              <Label>
                                <FormattedMessage defaultMessage="Verification code" />
                                <Input
                                  disableOnFocus
                                  disableAutocomplete
                                  type="text"
                                  placeholder={
                                    mfaRequested === MFA_TYPE_EMAIL
                                      ? intl.formatMessage({
                                          defaultMessage:
                                            'Code from the verification email',
                                        })
                                      : intl.formatMessage({
                                          defaultMessage:
                                            'Code from the MFA app',
                                        })
                                  }
                                  size="medium"
                                  maxLength={16}
                                  color={
                                    (touched &&
                                      error &&
                                      error !== QUIET_VALIDATION_FAILURE) ||
                                    (submitError && !dirtySinceLastSubmit)
                                      ? 'danger'
                                      : null
                                  }
                                  {...input}
                                />
                              </Label>
                            )}
                          </FormField>
                        )}
                        {registering && (
                          <>
                            <FormField
                              name="password"
                              validate={validateRequired}
                            >
                              {({
                                input,
                                meta: {
                                  touched,
                                  error,
                                  submitError,
                                  dirtySinceLastSubmit,
                                },
                              }) => (
                                <Label>
                                  <FormattedMessage defaultMessage="Password" />
                                  <Input
                                    disableOnFocus
                                    type={
                                      fieldVisibility['password']
                                        ? 'text'
                                        : 'password'
                                    }
                                    placeholder={intl.formatMessage({
                                      defaultMessage: 'Password',
                                    })}
                                    size="medium"
                                    maxLength={128}
                                    color={
                                      (touched &&
                                        error &&
                                        error !== QUIET_VALIDATION_FAILURE) ||
                                      (submitError && !dirtySinceLastSubmit)
                                        ? 'danger'
                                        : null
                                    }
                                    inputSuffix={
                                      <div className={styles.suffixIcons}>
                                        <Button
                                          transparent
                                          onClick={() =>
                                            toggleFieldVisibility('password')
                                          }
                                        >
                                          {fieldVisibility['password'] ? (
                                            <EyeOffIcon />
                                          ) : (
                                            <EyeIcon />
                                          )}
                                        </Button>
                                      </div>
                                    }
                                    {...input}
                                  />
                                </Label>
                              )}
                            </FormField>
                            <FormField
                              name="password_confirm"
                              validate={validateRequired}
                            >
                              {({
                                input,
                                meta: {
                                  touched,
                                  error,
                                  submitError,
                                  dirtySinceLastSubmit,
                                },
                              }) => (
                                <Label>
                                  <FormattedMessage defaultMessage="Confirm Password" />
                                  <Input
                                    disableOnFocus
                                    type={
                                      fieldVisibility['password_confirm']
                                        ? 'text'
                                        : 'password'
                                    }
                                    placeholder={intl.formatMessage({
                                      defaultMessage: 'Password',
                                    })}
                                    size="medium"
                                    maxLength={128}
                                    color={
                                      (touched &&
                                        error &&
                                        error !== QUIET_VALIDATION_FAILURE) ||
                                      (submitError && !dirtySinceLastSubmit)
                                        ? 'danger'
                                        : null
                                    }
                                    inputSuffix={
                                      <div className={styles.suffixIcons}>
                                        <Button
                                          transparent
                                          onClick={() =>
                                            toggleFieldVisibility(
                                              'password_confirm',
                                            )
                                          }
                                        >
                                          {fieldVisibility[
                                            'password_confirm'
                                          ] ? (
                                            <EyeOffIcon />
                                          ) : (
                                            <EyeIcon />
                                          )}
                                        </Button>
                                      </div>
                                    }
                                    {...input}
                                  />
                                </Label>
                              )}
                            </FormField>
                            <PasswordHints formProps={formProps} />
                          </>
                        )}
                        <div className={styles.actionButtons}>
                          <Button
                            submit
                            color="primary"
                            disabled={
                              submitting ||
                              (registering &&
                                (!passwordsMatch(
                                  formProps.values.password,
                                  formProps.values.password_confirm,
                                ) ||
                                  !passwordRequirementsMet(
                                    formProps.values.password,
                                  )))
                            }
                          >
                            {registering ? (
                              <FormattedMessage defaultMessage="Create account" />
                            ) : (
                              <FormattedMessage defaultMessage="Continue" />
                            )}
                          </Button>
                          {!registering && usernameChecked && !mfaRequested && (
                            <Button
                              color="secondary"
                              disabled={submitting}
                              onClick={() => {
                                passwordResetRedirect(
                                  formProps.values.username,
                                );
                              }}
                            >
                              <FormattedMessage defaultMessage="Forgot password?" />
                            </Button>
                          )}
                        </div>
                        {!registering && !usernameChecked && (
                          <div>
                            <span>
                              <FormattedMessage defaultMessage="Don’t have an account?" />
                            </span>
                            <button
                              onClick={() => onSignUp(formProps)}
                              className={styles.transparentButton}
                            >
                              <FormattedMessage defaultMessage="Sign up" />
                            </button>
                          </div>
                        )}
                      </form>
                    </div>
                  </>
                )}
              />
              <span className={styles.loginBody}>
                <span className={styles.textTOC}>
                  <FormattedMessage defaultMessage="By signing in or creating an account, you agree to the" />
                  <a target="_blank" rel="noreferrer" href={termsUrl}>
                    <FormattedMessage defaultMessage="Terms & Conditions," />
                  </a>
                  <a target="_blank" rel="noreferrer" href={privacyUrl}>
                    <FormattedMessage defaultMessage="Privacy Policy," />
                  </a>
                  <a target="_blank" rel="noreferrer" href={amlUrl}>
                    <FormattedMessage defaultMessage="AML/CTF Policy" />
                  </a>
                  <FormattedMessage defaultMessage=" and" />
                  <a target="_blank" rel="noreferrer" href={cookieUrl}>
                    <FormattedMessage defaultMessage="Cookie Policy" />
                  </a>
                </span>
              </span>
              <div className={styles.loginFooter} />
            </DialogBody>
          )}
        </>
      }
    />
  );
}
